import { ImportDialog } from "@/components/ImportDialog";
import { ProductMappingPanel } from "@/components/ProductMappingPanel";
import {
	CodeCell,
	StatusPill,
	UrlCell,
} from "@/components/commerce/grid-cells";
import { SectionNav } from "@/components/commerce/section-nav";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { PartyService } from "@/gen/omnios/v1/party_connect";
import { DiscountService } from "@/gen/omnios/v1/pricing_connect";
import { PromocodeService } from "@/gen/omnios/v1/pricing_connect";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";
import { createClient } from "@connectrpc/connect";

import type { ColDef, SelectionChangedEvent } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useServerGrid } from "@/lib/use-server-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ExternalLink, Loader2, Percent, Plus, Store, Tag } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type Section = "discounts" | "promocodes" | "competitors" | "mappings";

interface PricingSearch {
	tab?: Section;
}

export const Route = createFileRoute("/commerce/pricing")({
	validateSearch: (search: Record<string, unknown>): PricingSearch => ({
		tab: (search.tab as Section) || undefined,
	}),
	component: PricingPage,
});

const discountClient = createClient(DiscountService, transport);
const promocodeClient = createClient(PromocodeService, transport);
const partyClient = createClient(PartyService, transport);

function PricingPage() {
	const { t } = useTranslation("pricing");
	const navigate = useNavigate();
	const { tab } = Route.useSearch();
	const section: Section = tab || "discounts";

	const NAV_ITEMS: {
		value: Section;
		label: string;
		icon: typeof Percent;
	}[] = [
		{ value: "discounts", label: t("nav_discounts"), icon: Percent },
		{ value: "promocodes", label: t("nav_promocodes"), icon: Tag },
		{ value: "competitors", label: t("nav_competitors"), icon: Store },
		{ value: "mappings", label: t("nav_mappings"), icon: ExternalLink },
	];

	useBreadcrumbs([{ label: t("title") }]);

	function setSection(s: Section) {
		navigate({ to: "/commerce/pricing", search: { tab: s } });
	}

	return (
		<div className="flex h-full overflow-hidden">
			<SectionNav items={NAV_ITEMS} value={section} onChange={setSection} />

			<div className="flex-1 flex flex-col overflow-hidden">
				{section === "discounts" && <DiscountsPanel />}
				{section === "promocodes" && <PromocodesPanel />}
				{section === "competitors" && <CompetitorsPanel />}
				{section === "mappings" && <MappingsPanel />}
			</div>
		</div>
	);
}

// ── Discounts ───────────────────────────────

function DiscountsPanel() {
	const { t } = useTranslation("pricing");
	const qc = useQueryClient();
	const gridRef = useRef<AgGridReact>(null);
	const { onGridReady, onStateChanged } = useGridLayout("pricing-discounts");
	const [selectedRows, setSelectedRows] = useState<any[]>([]);
	const [showCreate, setShowCreate] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const [form, setForm] = useState({
		name: "",
		description: "",
		discountType: "percentage",
		percentage: 10,
		fixedCents: 0,
		priority: 0,
	});

	const { data, isLoading } = useQuery({
		queryKey: ["discounts"],
		queryFn: () => discountClient.listDiscounts({}),
	});

	const createMut = useMutation({
		mutationFn: () =>
			discountClient.createDiscount({
				name: form.name,
				description: form.description,
				discountType: form.discountType,
				percentage:
					form.discountType === "percentage" ? form.percentage : undefined,
				fixedCents:
					form.discountType === "fixed_amount"
						? BigInt(form.fixedCents)
						: undefined,
				priority: form.priority,
				targets: [{ targetType: "all", include: true }],
				conditions: [],
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["discounts"] });
			setShowCreate(false);
			setForm({
				name: "",
				description: "",
				discountType: "percentage",
				percentage: 10,
				fixedCents: 0,
				priority: 0,
			});
		},
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => discountClient.deleteDiscount({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["discounts"] });
			setDeleteTarget(null);
		},
	});

	const discounts = useMemo(() => data?.discounts ?? [], [data]);

	const columnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "name",
				headerName: t("col_name"),
				flex: 1,
				minWidth: 150,
				cellRenderer: (p: { data: any }) => {
					if (!p.data) return "";
					return (
						<div>
							<div style={{ fontWeight: 500 }}>{p.data.name}</div>
							{p.data.description && (
								<div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 300 }}>
									{p.data.description}
								</div>
							)}
						</div>
					);
				},
			},
			{
				field: "discountType",
				headerName: t("col_type"),
				width: 110,
				valueFormatter: (p) => {
					if (p.value === "percentage") return t("type_percentage");
					if (p.value === "fixed_amount") return t("type_fixed");
					if (p.value === "price_override") return t("type_override");
					return p.value ?? "";
				},
			},
			{
				colId: "value",
				headerName: t("col_value"),
				width: 100,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueGetter: (p) => {
					const d = p.data;
					if (!d) return "";
					if (d.discountType === "percentage") return `${d.percentage}%`;
					if (d.discountType === "fixed_amount")
						return `${(Number(d.fixedCents) / 100).toFixed(0)} \u20BD`;
					if (d.discountType === "price_override")
						return `${(Number(d.overrideCents) / 100).toFixed(0)} \u20BD`;
					return "";
				},
			},
			{
				field: "isActive",
				headerName: t("col_status"),
				width: 100,
				cellRenderer: (p: { value: boolean }) => (
					<StatusPill
						label={p.value ? t("status_active") : t("status_off")}
						color={
							p.value ? "hsl(var(--chart-2))" : "hsl(var(--muted-foreground))"
						}
					/>
				),
			},
			{
				colId: "actions",
				headerName: "",
				width: 50,
				suppressHeaderMenuButton: true,
				sortable: false,
				cellRenderer: (p: { data: any }) => {
					if (!p.data) return "";
					return (
						<button data-action="delete" style={{ cursor: "pointer", background: "none", border: "none", color: "hsl(var(--muted-foreground))", padding: 2 }} title={t("common:delete")}>
							{"\u2715"}
						</button>
					);
				},
				onCellClicked: (e) => {
					const target = e.event?.target as HTMLElement | undefined;
					if (target?.closest("[data-action='delete']"))
						setDeleteTarget(e.data.id);
				},
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	const onSelectionChanged = useCallback((e: SelectionChangedEvent) => {
		setSelectedRows(e.api.getSelectedRows());
	}, []);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content:
					selectedRows.length > 0
						? `${selectedRows.length} ${t("common:selected", { defaultValue: "\u0432\u044B\u0431\u0440\u0430\u043D\u043E" })}`
						: `${discounts.length} ${t("nav_discounts", { defaultValue: "\u0441\u043A\u0438\u0434\u043E\u043A" })}`,
				align: "left" as const,
			},
		],
		[discounts.length, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			<div className="flex items-center gap-2 px-4 py-2 shrink-0 justify-end">
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => qc.invalidateQueries({ queryKey: ["discounts"] })}
				>
					{t("common:refresh", { defaultValue: "Обновить" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: "discounts.csv",
						})
					}
				>
					{t("common:export", { defaultValue: "Экспорт" })}
				</Button>
				<Button size="sm" className="h-9" onClick={() => setShowCreate(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("common:create")}
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz h-full" style={{ width: "100%" }}>
				<AgGridReact
					ref={gridRef}
					rowData={discounts}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onGridReady={onGridReady}
					onSortChanged={onStateChanged}
					onFilterChanged={onStateChanged}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data.id}
					loading={isLoading}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("discounts_empty", { defaultValue: "\u041D\u0435\u0442 \u0441\u043A\u0438\u0434\u043E\u043A" })}</p></div>`}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("common:loading", { defaultValue: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430" })}...</span>`}
				/>
			</div>

			<Dialog open={showCreate} onOpenChange={setShowCreate}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("new_discount")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label>{t("col_name")}</Label>
							<Input
								value={form.name}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
								placeholder={t("discount_name_placeholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("common:description")}</Label>
							<Textarea
								value={form.description}
								onChange={(e) =>
									setForm({ ...form, description: e.target.value })
								}
								placeholder={t("discount_desc_placeholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("col_type")}</Label>
							<Select
								value={form.discountType}
								onValueChange={(v) => setForm({ ...form, discountType: v })}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="percentage">
										{t("type_percentage")}
									</SelectItem>
									<SelectItem value="fixed_amount">
										{t("fixed_amount")}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{form.discountType === "percentage" ? (
							<div className="space-y-2">
								<Label>{t("percentage_label")}</Label>
								<Input
									type="number"
									min={0}
									max={100}
									value={form.percentage}
									onChange={(e) =>
										setForm({ ...form, percentage: Number(e.target.value) })
									}
								/>
							</div>
						) : (
							<div className="space-y-2">
								<Label>{t("amount_cents_label")}</Label>
								<Input
									type="number"
									min={0}
									value={form.fixedCents}
									onChange={(e) =>
										setForm({ ...form, fixedCents: Number(e.target.value) })
									}
								/>
							</div>
						)}
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setShowCreate(false)}>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() => createMut.mutate()}
							disabled={!form.name || createMut.isPending}
						>
							{createMut.isPending && (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							)}
							{t("common:create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(o) => !o && setDeleteTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("delete_discount")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("action_irreversible")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => deleteTarget && deleteMut.mutate(deleteTarget)}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}

// ── Promocodes ──────────────────────────────

function PromocodesPanel() {
	const { t } = useTranslation("pricing");
	const qc = useQueryClient();
	const gridRef = useRef<AgGridReact>(null);
	const { onGridReady, onStateChanged } = useGridLayout("pricing-promocodes");
	const [selectedRows, setSelectedRows] = useState<any[]>([]);
	const [showCreate, setShowCreate] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const [form, setForm] = useState({
		code: "",
		name: "",
		discountType: "percentage",
		percentage: 10,
		fixedCents: 0,
		isPublic: true,
	});

	const { data, isLoading } = useQuery({
		queryKey: ["promocodes"],
		queryFn: () => promocodeClient.listPromocodes({}),
	});

	const createMut = useMutation({
		mutationFn: () =>
			promocodeClient.createPromocode({
				code: form.code.toUpperCase(),
				name: form.name,
				inlineDiscountType: form.discountType,
				inlinePercentage:
					form.discountType === "percentage" ? form.percentage : undefined,
				inlineFixedCents:
					form.discountType === "fixed_amount"
						? BigInt(form.fixedCents)
						: undefined,
				isPublic: form.isPublic,
				appliesTo: "order",
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["promocodes"] });
			setShowCreate(false);
			setForm({
				code: "",
				name: "",
				discountType: "percentage",
				percentage: 10,
				fixedCents: 0,
				isPublic: true,
			});
		},
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => promocodeClient.deletePromocode({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["promocodes"] });
			setDeleteTarget(null);
		},
	});

	const promocodes = useMemo(() => data?.promocodes ?? [], [data]);

	const columnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "code",
				headerName: t("col_code"),
				width: 140,
				pinned: "left",
				cellClass: "font-mono",
				cellRenderer: (p: { value: string }) => <CodeCell value={p.value} />,
			},
			{
				field: "name",
				headerName: t("col_name"),
				flex: 1,
				minWidth: 150,
				valueFormatter: (p) => p.value || "\u2014",
			},
			{
				colId: "discount",
				headerName: t("col_discount"),
				width: 100,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueGetter: (p) => {
					const d = p.data;
					if (!d) return "";
					if (d.inlineDiscountType === "percentage")
						return `${d.inlinePercentage}%`;
					if (d.inlineDiscountType === "fixed_amount")
						return `${(Number(d.inlineFixedCents) / 100).toFixed(0)} \u20BD`;
					return "";
				},
			},
			{
				field: "isActive",
				headerName: t("col_status"),
				width: 100,
				cellRenderer: (p: { value: boolean }) => (
					<StatusPill
						label={p.value ? t("status_promo_active") : t("status_off")}
						color={
							p.value ? "hsl(var(--chart-2))" : "hsl(var(--muted-foreground))"
						}
					/>
				),
			},
			{
				colId: "actions",
				headerName: "",
				width: 50,
				suppressHeaderMenuButton: true,
				sortable: false,
				cellRenderer: (p: { data: any }) => {
					if (!p.data) return "";
					return (
						<button data-action="delete" style={{ cursor: "pointer", background: "none", border: "none", color: "hsl(var(--muted-foreground))", padding: 2 }}>
							{"\u2715"}
						</button>
					);
				},
				onCellClicked: (e) => {
					const target = e.event?.target as HTMLElement | undefined;
					if (target?.closest("[data-action='delete']"))
						setDeleteTarget(e.data.id);
				},
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	const onSelectionChanged = useCallback((e: SelectionChangedEvent) => {
		setSelectedRows(e.api.getSelectedRows());
	}, []);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content:
					selectedRows.length > 0
						? `${selectedRows.length} ${t("common:selected", { defaultValue: "\u0432\u044B\u0431\u0440\u0430\u043D\u043E" })}`
						: `${promocodes.length} ${t("nav_promocodes", { defaultValue: "\u043F\u0440\u043E\u043C\u043E\u043A\u043E\u0434\u043E\u0432" })}`,
				align: "left" as const,
			},
		],
		[promocodes.length, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			<div className="flex items-center gap-2 px-4 py-2 shrink-0 justify-end">
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => qc.invalidateQueries({ queryKey: ["promocodes"] })}
				>
					{t("common:refresh", { defaultValue: "Обновить" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: "promocodes.csv",
						})
					}
				>
					{t("common:export", { defaultValue: "Экспорт" })}
				</Button>
				<Button size="sm" className="h-9" onClick={() => setShowCreate(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("common:create")}
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz h-full" style={{ width: "100%" }}>
				<AgGridReact
					ref={gridRef}
					rowData={promocodes}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onGridReady={onGridReady}
					onSortChanged={onStateChanged}
					onFilterChanged={onStateChanged}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data.id}
					loading={isLoading}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("promocodes_empty", { defaultValue: "\u041D\u0435\u0442 \u043F\u0440\u043E\u043C\u043E\u043A\u043E\u0434\u043E\u0432" })}</p></div>`}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("common:loading", { defaultValue: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430" })}...</span>`}
				/>
			</div>

			<Dialog open={showCreate} onOpenChange={setShowCreate}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("new_promocode")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label>{t("col_code")}</Label>
							<Input
								value={form.code}
								onChange={(e) =>
									setForm({ ...form, code: e.target.value.toUpperCase() })
								}
								placeholder="SUMMER2024"
								className="uppercase"
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("col_name")}</Label>
							<Input
								value={form.name}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
								placeholder={t("promo_name_placeholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("discount_type_label")}</Label>
							<Select
								value={form.discountType}
								onValueChange={(v) => setForm({ ...form, discountType: v })}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="percentage">
										{t("type_percentage")}
									</SelectItem>
									<SelectItem value="fixed_amount">
										{t("fixed_amount")}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{form.discountType === "percentage" ? (
							<div className="space-y-2">
								<Label>{t("percentage_label")}</Label>
								<Input
									type="number"
									min={0}
									max={100}
									value={form.percentage}
									onChange={(e) =>
										setForm({ ...form, percentage: Number(e.target.value) })
									}
								/>
							</div>
						) : (
							<div className="space-y-2">
								<Label>{t("amount_cents_label")}</Label>
								<Input
									type="number"
									min={0}
									value={form.fixedCents}
									onChange={(e) =>
										setForm({ ...form, fixedCents: Number(e.target.value) })
									}
								/>
							</div>
						)}
						<div className="flex items-center justify-between">
							<Label>{t("public_label")}</Label>
							<Switch
								checked={form.isPublic}
								onCheckedChange={(v) => setForm({ ...form, isPublic: v })}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setShowCreate(false)}>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() => createMut.mutate()}
							disabled={!form.code || createMut.isPending}
						>
							{createMut.isPending && (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							)}
							{t("common:create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(o) => !o && setDeleteTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("delete_promocode")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("action_irreversible")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => deleteTarget && deleteMut.mutate(deleteTarget)}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}

// ── Competitors ─────────────────────────────

function CompetitorsPanel() {
	const { t } = useTranslation("pricing");
	const { onGridReady, onStateChanged } = useGridLayout("pricing-competitors");
	const [selectedRows, setSelectedRows] = useState<any[]>([]);
	const [showCreate, setShowCreate] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const [importCompetitorId, setImportCompetitorId] = useState<string | null>(
		null,
	);
	const [form, setForm] = useState({
		name: "",
		website: "",
		feedUrl: "",
		feedType: "yandex_market",
	});

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<any>({
		fetchRows: async ({ pageSize, pageToken }) => {
			const resp = await partyClient.listParties({
				role: "competitor",
				page: { pageSize, pageToken },
			});
			return {
				rows: resp.parties as any[],
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		colToField: {
			isActive: "is_active",
		},
	});

	const createMut = useMutation({
		mutationFn: async () => {
			const party = await partyClient.createParty({
				name: form.name,
				kind: 1,
				attrs: {
					fields: {
						website: {
							kind: { case: "stringValue", value: form.website || "" },
						},
						feed_url: {
							kind: { case: "stringValue", value: form.feedUrl || "" },
						},
						feed_type: {
							kind: { case: "stringValue", value: form.feedType || "" },
						},
					},
				},
			});
			return party;
		},
		onSuccess: () => {
			refreshGrid();
			setShowCreate(false);
			setForm({
				name: "",
				website: "",
				feedUrl: "",
				feedType: "yandex_market",
			});
		},
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => partyClient.deleteParty({ id }),
		onSuccess: () => {
			refreshGrid();
			setDeleteTarget(null);
		},
	});

	const columnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "name",
				headerName: t("col_name"),
				flex: 1,
				minWidth: 150,
			},
			{
				field: "website",
				headerName: t("col_website"),
				flex: 1,
				minWidth: 150,
				cellRenderer: (p: { value: string }) => <UrlCell value={p.value} />,
			},
			{
				field: "feedType",
				headerName: t("col_feed_type"),
				width: 130,
				valueFormatter: (p) => {
					if (p.value === "yandex_market") return t("feed_yandex");
					if (p.value === "google_merchant") return "Google Merchant";
					if (p.value === "manual") return t("feed_manual");
					return p.value || "\u2014";
				},
			},
			{
				field: "isActive",
				headerName: t("col_status"),
				width: 100,
				filter: false,
				cellRenderer: (p: { value: boolean }) => (
					<StatusPill
						label={p.value ? t("status_promo_active") : t("status_off")}
						color={
							p.value ? "hsl(var(--chart-2))" : "hsl(var(--muted-foreground))"
						}
					/>
				),
			},
			{
				colId: "actions",
				headerName: "",
				width: 100,
				suppressHeaderMenuButton: true,
				sortable: false,
				filter: false,
				cellRenderer: (p: { data: any }) => {
					if (!p.data) return "";
					return (
						<div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
							<button data-action="import" style={{ cursor: "pointer", background: "none", border: "none", color: "var(--primary)", fontSize: 12, padding: "2px 4px" }}>
								{t("import_btn")}
							</button>
							<button data-action="delete" style={{ cursor: "pointer", background: "none", border: "none", color: "hsl(var(--muted-foreground))", padding: 2 }}>
								{"\u2715"}
							</button>
						</div>
					);
				},
				onCellClicked: (e) => {
					const target = e.event?.target as HTMLElement | undefined;
					const action = target
						?.closest("[data-action]")
						?.getAttribute("data-action");
					if (action === "import") setImportCompetitorId(e.data.id);
					else if (action === "delete") setDeleteTarget(e.data.id);
				},
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	const onSelectionChanged = useCallback((e: SelectionChangedEvent) => {
		setSelectedRows(e.api.getSelectedRows());
	}, []);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content:
					selectedRows.length > 0
						? `${selectedRows.length} ${t("common:selected", { defaultValue: "\u0432\u044B\u0431\u0440\u0430\u043D\u043E" })}`
						: `${totalRows} ${t("nav_competitors", { defaultValue: "\u043A\u043E\u043D\u043A\u0443\u0440\u0435\u043D\u0442\u043E\u0432" })}`,
				align: "left" as const,
			},
		],
		[totalRows, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			<div className="flex items-center gap-2 px-4 py-2 shrink-0 justify-end">
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => refreshGrid()}
				>
					{t("common:refresh", { defaultValue: "Обновить" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: "competitors.csv",
						})
					}
				>
					{t("common:export", { defaultValue: "Экспорт" })}
				</Button>
				<Button size="sm" className="h-9" onClick={() => setShowCreate(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("common:add")}
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz h-full" style={{ width: "100%" }}>
				<AgGridReact
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onGridReady={onGridReady}
					onSortChanged={() => { onStateChanged(); refreshGrid(); }}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data?.id}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("competitors_empty", { defaultValue: "\u041D\u0435\u0442 \u043A\u043E\u043D\u043A\u0443\u0440\u0435\u043D\u0442\u043E\u0432" })}</p></div>`}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("common:loading", { defaultValue: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430" })}...</span>`}
				/>
			</div>

			<Dialog open={showCreate} onOpenChange={setShowCreate}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("new_competitor")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label>{t("col_name")}</Label>
							<Input
								value={form.name}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
								placeholder={t("competitor_name_placeholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("website_label")}</Label>
							<Input
								value={form.website}
								onChange={(e) => setForm({ ...form, website: e.target.value })}
								placeholder="https://vseinstrumenti.ru"
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("feed_url_label")}</Label>
							<Input
								value={form.feedUrl}
								onChange={(e) => setForm({ ...form, feedUrl: e.target.value })}
								placeholder="https://vseinstrumenti.ru/feed.xml"
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("feed_type_label")}</Label>
							<Select
								value={form.feedType}
								onValueChange={(v) => setForm({ ...form, feedType: v })}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="yandex_market">
										{t("feed_yandex")}
									</SelectItem>
									<SelectItem value="google_merchant">
										Google Merchant
									</SelectItem>
									<SelectItem value="manual">
										{t("feed_manual_input")}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setShowCreate(false)}>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() => createMut.mutate()}
							disabled={!form.name || createMut.isPending}
						>
							{createMut.isPending && (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							)}
							{t("common:add")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(o) => !o && setDeleteTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("delete_competitor")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("delete_competitor_hint")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => deleteTarget && deleteMut.mutate(deleteTarget)}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<ImportDialog
				open={!!importCompetitorId}
				onOpenChange={(v) => !v && setImportCompetitorId(null)}
				defaultDestination="competitor"
				defaultCompetitorId={importCompetitorId ?? undefined}
			/>
		</>
	);
}

// ── Mappings ─────────────────────────────────

function MappingsPanel() {
	const { t } = useTranslation("pricing");
	const [selectedCompetitorId, setSelectedCompetitorId] = useState<string>("");

	const { data } = useQuery({
		queryKey: ["parties", "competitor"],
		queryFn: () => partyClient.listParties({ role: "competitor" }),
	});

	const competitors = (data?.parties ?? []) as any[];

	return (
		<div className="space-y-4 p-6">
			<div className="flex items-center gap-4">
				<h3 className="text-sm font-medium">{t("mappings_title")}</h3>
				<Select
					value={selectedCompetitorId}
					onValueChange={setSelectedCompetitorId}
				>
					<SelectTrigger className="w-56">
						<SelectValue placeholder={t("select_competitor")} />
					</SelectTrigger>
					<SelectContent>
						{competitors.map((c) => (
							<SelectItem key={c.id} value={c.id}>
								{c.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{selectedCompetitorId ? (
				<ProductMappingPanel
					sourceType="competitor"
					sourceId={selectedCompetitorId}
					sourceName={
						competitors.find((c) => c.id === selectedCompetitorId)?.name ?? ""
					}
				/>
			) : (
				<p className="text-sm text-muted-foreground py-8 text-center">
					{t("select_competitor_hint")}
				</p>
			)}
		</div>
	);
}
